import { useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { cn } from "@/lib/utils"
import { layoutContainerClassName } from "./layout/constants"
import { Header } from "./layout/header"
import { Footer } from "./layout/footer"
import { MobileBackBar, MobileTitleRow } from "./layout/mobile-bars"
import { TabBar } from "./layout/tab-bar"

export type LayoutProps = {
  children: React.ReactNode
  /** Mobile large title. Omit on pages that use `back`. */
  title?: string
  /** Mobile trailing action on the title row (e.g. the + button). */
  action?: React.ReactNode
  /** Mobile top bar with a back link instead of a large title. */
  back?: { to: string; label: string; trailing?: React.ReactNode }
  /** Show the mobile tab bar. Default true. */
  tabBar?: boolean
}

// Desktop ignores `title`, `action`, `back` and `tabBar`: header, content,
// footer. The phone layout drops the header and footer for a large title (or
// a back bar) and a bottom tab bar.
export const Layout = ({
  children,
  title,
  action,
  back,
  tabBar = true,
}: LayoutProps) => {
  const [displayName, setDisplayName] = useState("")

  useEffect(() => {
    const applyUser = (
      user:
        | { user_metadata?: { display_name?: string }; email?: string }
        | null
        | undefined
    ) => {
      setDisplayName(user?.user_metadata?.display_name || user?.email || "")
    }

    supabase.auth
      .getSession()
      .then(({ data }) => applyUser(data.session?.user))
      .catch(() => {})

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      applyUser(session?.user)
    })

    return () => {
      sub.subscription.unsubscribe()
    }
  }, [])

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = "/auth/signin"
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="hidden desktop:block">
        <Header displayName={displayName} onSignOut={handleSignOut} />
      </div>
      {back && <MobileBackBar {...back} />}
      <main className="flex-1">
        <div
          className={cn(
            layoutContainerClassName,
            "py-6",
            // Below the status bar, unless the back bar already clears it.
            !back && "pt-[calc(1.5rem+env(safe-area-inset-top))]",
            // Clear the tab bar, or just the home indicator without one.
            tabBar
              ? "pb-[calc(1.5rem+var(--tab-bar-height))]"
              : "pb-[calc(1.5rem+env(safe-area-inset-bottom))]",
            "desktop:py-6"
          )}
        >
          {!back && title && <MobileTitleRow title={title} action={action} />}
          {children}
        </div>
      </main>
      <div className="hidden desktop:block">
        <Footer />
      </div>
      {tabBar && <TabBar />}
    </div>
  )
}
